// Task row — dispatches between juuplan-Komfort (luftiger, Pill-Status,
// Range-Datum) und juuplan-Kompakt (alter Monday-Look mit Full-Cell-Fills)
// via lib/layout. Die Mobile-Card oben ist beiden gemeinsam, weil sie
// ohnehin schon im Pill-Stil rendert.

import {
  AlertCircle,
  Calendar,
  Check,
  ChevronDown,
  CircleDot,
  Diamond,
  Flag,
  GripVertical,
  Paperclip,
  Waypoints,
} from "lucide-preact";
import type { ApiWorkItemType, LabelItem, Status, StatusInfo, Task, TaskPriority, TaskStatus } from "@juuplan/shared";
import {
  TASK_PRIORITY_LABEL,
  TASK_PRIORITY_VAR,
  TASK_STATUS_LABEL,
  TASK_STATUS_VAR,
  formatDateShort,
  softPillStyle,
} from "../lib/format";
import { useLayout } from "../lib/layout";
import {
  GRID_GAP_CLASS,
  GRID_PADDING_X_PX,
  ROW_BG_BASE,
  ROW_BG_SELECTED,
  ROW_BG_VAR,
  frozenMinWidthPx,
  gridMinWidth,
  gridSpacingClass,
  splitGridTemplate,
  type TableColumnId,
} from "../lib/columns";
import { AssigneePicker, AvatarStack } from "./AssigneePicker";
import { LabelChip } from "./LabelChip";
import { WorkItemTypeBadge } from "./WorkItemTypeBadge";
import { AiOriginBadge } from "./AiOriginBadge";
import { UserAvatar } from "./UserAvatar";
import { findUser } from "../store/users";
import { RowActions } from "./RowActions";
import { TaskDateCell } from "./TaskDateCell";
import { TaskRowKompakt } from "./TaskRowKompakt";

interface Props {
  task: Task;
  onOpen: () => void;
  /** Title is edited inline only inside TaskModal — kept on the props for
   *  symmetry with future kanban/calendar callbacks. */
  onTitle?: (next: string) => void | Promise<void>;
  onStatus: (next: TaskStatus) => void | Promise<void>;
  /** Called with the custom status id when the project uses dynamic statuses. */
  onStatusId?: (id: number) => void | Promise<void>;
  onAssignees: (next: number[]) => void | Promise<void>;
  onStartDate: (next: string | null) => void | Promise<void>;
  onEndDate: (next: string | null) => void | Promise<void>;
  onPriority: (next: TaskPriority | null) => void | Promise<void>;
  /** Delete the task — when provided (and not readOnly), a trash button
   *  appears in the row's hover-action cluster on the right. */
  onDelete?: () => void | Promise<void>;
  /** UX-Vorschlag P6: Zeilenaktion „Sub-Aufgabe anlegen" (nur mit Flag). */
  onAddSubtask?: () => void;
  /** Owning project id — passed through to AssigneePicker so it can default
   *  to "project members" instead of every active user (#875). Optional so
   *  rows rendered outside a project context (e.g. personal-task lists) keep
   *  today's "all users" behavior untouched. */
  projectId?: number;
  dragHandleProps?: Record<string, unknown>;
  /** Columns the user has hidden via the toolbar; cells with these ids are not rendered. */
  hiddenColumns?: Set<TableColumnId>;
  /** Project label cache (id → LabelItem) — used to resolve label_ids on rows. */
  labelsById?: Map<number, LabelItem>;
  /** Project work-item-type cache (id → type) — resolves task.type_id for the Typ column. */
  typesById?: Map<number, ApiWorkItemType>;
  /** Project's custom statuses (sorted by sortOrder). When provided and the task
   *  has statusInfo, the status cell uses these instead of the legacy enum. */
  statuses?: Status[];
  /** Viewer-Rolle: inline-Edits (Status, Zuständig, Daten, Prio, Done) gesperrt. */
  readOnly?: boolean;
  /** Bulk-select: whether this row is currently selected. */
  selected?: boolean;
  /** Bulk-select: callback to toggle selection for this row. */
  onSelect?: (id: number) => void;
  /** Bulk-select: whether any row in the list is currently selected (used to
   *  keep checkboxes visible when a selection is active). */
  anySelected?: boolean;
}

export function TaskRow(props: Props): preact.JSX.Element {
  const layout = useLayout();
  if (layout === "kompakt") {
    return <TaskRowKompakt {...props} />;
  }
  return <TaskRowKomfort {...props} />;
}

function TaskRowKomfort(props: Props): preact.JSX.Element {
  const {
    task,
    onOpen,
    onStatus,
    onStatusId,
    onAssignees,
    onStartDate,
    onEndDate,
    onPriority,
    onDelete,
    projectId,
    dragHandleProps,
    hiddenColumns,
    labelsById,
    typesById,
    statuses,
    readOnly = false,
    selected = false,
    onSelect,
    anySelected = false,
  } = props;

  const hidden = hiddenColumns ?? new Set<TableColumnId>();
  const { frozen: frozenTemplate, scrolling: scrollingTemplate } =
    splitGridTemplate(hidden, "komfort");

  const workItemType =
    task.type_id != null ? (typesById?.get(task.type_id) ?? null) : null;

  const labels: LabelItem[] = labelsById
    ? task.label_ids
        .map((id) => labelsById.get(id))
        .filter((l): l is LabelItem => l !== undefined)
    : [];

  const isDone = task.status === "done";
  const isBlocked = !task.is_milestone && task.status === "blocked";

  const toggleDone = (e: Event): void => {
    e.stopPropagation();
    if (readOnly) return;
    // Toggle: Done ⇄ in_progress. Andere Stati bleiben unverändert wenn der
    // User explizit den Kreis tappt — Klick auf den Status-Pill rechts ist
    // der Weg für Backlog/Review/Blocked.
    if (isDone) void onStatus("in_progress");
    else void onStatus("done");
  };

  return (
    <>
      <MobileTaskCard task={task} labels={labels} onOpen={onOpen} onToggleDone={toggleDone} readOnly={readOnly} />
      <div
        class={`juuplan-row juuplan-row-komfort hidden md:flex group ${gridSpacingClass("komfort")} items-center py-2.5 text-sm border-b border-[color:var(--color-outline-variant)]/40 bg-[color:var(--juuplan-row-bg)] hover:bg-[color:var(--color-surface-container-low)] hover:shadow-[inset_3px_0_0_var(--color-primary)] transition-all duration-100`}
        style={{
          minHeight: "52px",
          minWidth: gridMinWidth(hidden, "komfort"),
          // Deckender Zeilen-Hintergrund als Variable, damit die hover:-Klasse
          // weiter gewinnt und der eingefrorene Block ihn erben kann.
          [ROW_BG_VAR]: selected ? ROW_BG_SELECTED : ROW_BG_BASE,
        }}
        /* EINE GESTE ZUM ÖFFNEN: einfacher Klick auf den Titel (der Titel ist
           ein Button, siehe unten) — plus die Hover-Aktion „Bearbeiten" rechts.
           Der vorherige `onDblClick` auf der GANZEN Zeile war die zweite Geste
           für dieselbe Handlung: `RecordRow` öffnet mit einem Klick, diese
           Zeile brauchte zwei — auf demselben Bildschirm. Doppelklick ist
           zudem auf Touch unzuverlässig (er wird dort zu Zoom/Auswahl), und
           ein Klick auf die ganze Zeile geht hier nicht: die Zeile ist voll
           von Feldern, die selbst Klicks brauchen (Datum, Status, Priorität,
           Zuständige). */
      >
        {/* Eingefrorener Block: Griff, Erledigt-Kreis, Titel. Bleibt beim
            waagerechten Scrollen links stehen (sticky greift hier, weil der
            Block ein FLEX-Item ist — als Grid-Zelle wäre sticky wirkungslos,
            siehe lib/columns.ts). Der negative linke Rand plus gleich großer
            Innenabstand zieht den deckenden Hintergrund bis an die Kante. */}
        <div
          class={`sticky left-0 z-[1] grid grow items-center ${GRID_GAP_CLASS.komfort} transition-colors duration-100`}
          style={{
            gridTemplateColumns: frozenTemplate,
            flexBasis: 0,
            minWidth: `${frozenMinWidthPx("komfort")}px`,
            marginLeft: `-${GRID_PADDING_X_PX.komfort}px`,
            paddingLeft: `${GRID_PADDING_X_PX.komfort}px`,
            backgroundColor: "inherit",
          }}
        >
        {/* Drag handle + selection checkbox — beide teilen sich die erste
            Spalte. Früher war das ein Entweder/Oder: sobald onSelect gesetzt
            war (Bulk-Select), verdrängte die Checkbox den Drag-Griff komplett,
            sodass @hello-pangea/dnd kein Griff-Element fand und Tasks sich
            nicht mehr zwischen Phasen ziehen ließen. Jetzt koexistieren beide;
            der leere Griff-Slot reserviert die Breite, damit die Checkboxen
            über alle Zeilen (auch Sub-Tasks ohne Griff) ausgerichtet bleiben. */}
        <span class="flex items-center gap-1">
          {!readOnly && dragHandleProps ? (
            <span
              {...dragHandleProps}
              class="cursor-grab shrink-0 text-[color:var(--color-on-surface-variant)] opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Aufgabe verschieben"
              title="Zwischen Phasen verschieben"
            >
              <GripVertical size={14} aria-hidden="true" />
            </span>
          ) : (
            <span class="inline-block shrink-0" style={{ width: "14px" }} aria-hidden="true" />
          )}
          {onSelect && (
            <input
              type="checkbox"
              checked={selected}
              onChange={(e) => { e.stopPropagation(); onSelect(task.id); }}
              onClick={(e) => e.stopPropagation()}
              class={`size-3.5 shrink-0 accent-[color:var(--color-primary)] cursor-pointer transition-opacity ${selected || anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-visible:opacity-100"}`}
              aria-label={`Aufgabe „${task.title}" auswählen`}
            />
          )}
        </span>

        {/* Done circle / milestone diamond */}
        <button
          type="button"
          onClick={toggleDone}
          disabled={readOnly}
          class="flex items-center justify-center rounded-full hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 transition-colors disabled:cursor-default disabled:hover:bg-transparent"
          style={{ width: "24px", height: "24px" }}
          aria-label={isDone ? "Aufgabe wieder öffnen" : "Aufgabe als erledigt markieren"}
          title={isDone ? "Wieder öffnen" : "Erledigt markieren"}
        >
          {task.is_milestone ? (
            <Diamond
              size={16}
              class="text-[color:var(--color-prio-urgent)]"
              fill="currentColor"
              aria-hidden="true"
            />
          ) : isDone ? (
            <span
              class="inline-flex items-center justify-center rounded-full text-[color:var(--color-on-accent)]"
              style={{
                width: "18px",
                height: "18px",
                background: TASK_STATUS_VAR.done,
              }}
            >
              <Check size={14} strokeWidth={3} aria-hidden="true" />
            </span>
          ) : (
            <span
              class="inline-block rounded-full border-2"
              style={{
                width: "18px",
                height: "18px",
                borderColor: "var(--color-outline-variant)",
              }}
              aria-hidden="true"
            />
          )}
        </button>

        {/* Title cluster — title + inline badges */}
        <button
          type="button"
          onClick={onOpen}
          class="inline-flex min-w-0 items-center gap-2 text-left text-base text-[color:var(--color-on-surface)] truncate rounded px-2 -mx-2 py-1 hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
          title={isBlocked ? `${task.title} (blockiert)` : task.title}
        >
          {isBlocked && (
            <AlertCircle
              size={14}
              class="text-[color:var(--color-status-blocked)] shrink-0"
              aria-label="Blockiert"
            />
          )}
          {/* min-w-0 lets the title ellipsize so the trailing badges (ticket #,
              AI-Origin, Anhänge …) stay visible instead of being pushed out of
              the truncating cluster. */}
          <span class={`min-w-0 truncate ${isDone ? "line-through opacity-60" : ""}`}>
            {task.title}
          </span>
          {/* Ticketnummer inline nur, wenn die eigene Ticket-Spalte ausgeblendet
              ist — sonst stand die Nummer zweimal in derselben Zeile (#833). */}
          {task.ticket && hidden.has("ticket") && (
            <span
              class="font-mono text-label font-medium text-[color:var(--color-on-surface-variant)] shrink-0"
              title="Ticketnummer"
            >
              {task.ticket}
            </span>
          )}
          <AiOriginBadge origin={task.ai_origin} />
          {task.attachment_count > 0 && (
            <span
              class="shrink-0 inline-flex items-center gap-0.5 text-xs text-[color:var(--color-on-surface-variant)]"
              title={`${task.attachment_count} Anhang${task.attachment_count === 1 ? "" : "änge"}`}
              aria-label={`${task.attachment_count} Anhänge`}
            >
              <Paperclip size={14} aria-hidden="true" />
              {task.attachment_count}
            </span>
          )}
          {task.subtask_count > 0 && (
            <span
              class="shrink-0 inline-flex items-center gap-0.5 text-xs text-[color:var(--color-on-surface-variant)]"
              title={`${task.subtask_count} Unteraufgaben`}
              aria-label={`${task.subtask_count} Unteraufgaben`}
            >
              <CircleDot size={14} aria-hidden="true" />
              {task.subtask_count}
            </span>
          )}
          {task.dependency_count > 0 && (
            <span
              class="shrink-0 inline-flex items-center gap-0.5 text-xs text-[color:var(--color-on-surface-variant)]"
              title={`${task.dependency_count} Abhängigkeit${task.dependency_count === 1 ? "" : "en"}`}
              aria-label={`${task.dependency_count} Abhängigkeiten`}
            >
              <Waypoints size={14} aria-hidden="true" />
              {task.dependency_count}
            </span>
          )}
        </button>
        </div>

        {/* Mitscrollender Block: alle übrigen Spalten. `shrink-0`, damit die
            festen Spurbreiten nicht gequetscht werden — zu breit heißt
            waagerecht scrollen (der Container hat overflow-x-auto). */}
        <div
          class={`grid shrink-0 items-center ${GRID_GAP_CLASS.komfort}`}
          style={{ gridTemplateColumns: scrollingTemplate }}
        >
        {/* Ticket number — dedicated, toggleable column. Ist sie sichtbar,
            entfällt der Inline-Badge im Titel-Cluster oben (#833). */}
        {!hidden.has("ticket") && (
          <span
            class="select-text truncate font-mono text-label font-medium text-[color:var(--color-on-surface-variant)]"
            title={task.ticket ?? undefined}
          >
            {task.ticket ?? "—"}
          </span>
        )}

        {/* Work-item type — colored pill, resolved from the project type cache */}
        {!hidden.has("type") && (
          <span class="inline-flex items-center min-w-0 justify-self-start">
            <WorkItemTypeBadge type={workItemType} />
          </span>
        )}

        {/* Status — pill, not full-cell-fill */}
        {!hidden.has("status") && (
          <StatusPill
            value={task.status}
            statusInfo={task.statusInfo ?? null}
            statuses={statuses}
            onChange={onStatus}
            onChangeId={onStatusId}
            readOnly={readOnly}
          />
        )}

        {/* Assignees — avatar stack via AssigneePicker compact-mode. Im
            Read-Only-Modus statischer Avatar-Stack ohne Picker.
            `emptyLabel="—"`: der Standardtext „— Nicht zugewiesen —" brach in
            dieser schmalen Spalte auf DREI Zeilen um und trieb damit jede
            Zeile über 60 px. Der Zustand „nicht zugewiesen" steht jetzt als
            dezenter Platzhalter da; benannt bleibt das Feld über das
            `aria-label` und den `title` des Auslösers, die Zuweisungslogik
            (M:N, Mehrfachauswahl) ist unberührt. */}
        {!hidden.has("assignees") &&
          (readOnly ? (
            <span class="inline-flex items-center min-h-[28px]">
              <AvatarStack userIds={task.assignee_ids} />
            </span>
          ) : (
            <AssigneePicker
              value={task.assignee_ids}
              onChange={onAssignees}
              compact
              label="Zuständige"
              emptyLabel="—"
              projectId={projectId}
            />
          ))}

        {/* Labels */}
        {!hidden.has("labels") && (
          <LabelsCell labels={labels} onOpen={onOpen} />
        )}

        {/* Start date */}
        {!hidden.has("start") && (
          <TaskDateCell value={task.start_date} onChange={onStartDate} ariaLabel="Startdatum" readOnly={readOnly} />
        )}

        {/* End date */}
        {!hidden.has("end") && (
          <TaskDateCell value={task.end_date} onChange={onEndDate} ariaLabel="Enddatum" readOnly={readOnly} />
        )}

        {/* Priority — pill, dashed outline when unset */}
        {!hidden.has("priority") && (
          <PriorityPill value={task.priority ?? null} onChange={onPriority} readOnly={readOnly} />
        )}

        {/* Hover-Aktionen: Bearbeiten + Löschen (rechts, erscheinen bei Hover) */}
        {!readOnly && onDelete ? (
          <RowActions title={task.title} onEdit={onOpen} onDelete={onDelete} onAddSubtask={props.onAddSubtask} />
        ) : (
          <span aria-hidden="true" />
        )}
        </div>
      </div>
    </>
  );
}

// Compact card variant rendered on <768px (gleich in beiden Layouts).
function MobileTaskCard(props: {
  task: Task;
  labels: LabelItem[];
  onOpen: () => void;
  onToggleDone?: (e: Event) => void;
  readOnly?: boolean;
}): preact.JSX.Element {
  const { task, labels, onOpen, onToggleDone, readOnly = false } = props;
  const assignees = task.assignee_ids
    .map((id) => findUser(id))
    .filter((u): u is NonNullable<ReturnType<typeof findUser>> => u !== null)
    .slice(0, 3);
  const extraAssignees = Math.max(0, task.assignee_ids.length - assignees.length);
  const due = task.end_date ?? task.start_date;
  const isDone = task.status === "done";
  const isBlocked = !task.is_milestone && task.status === "blocked";

  // The card is a div (not a button) so it can host a real done-toggle button
  // inside it — nesting <button> in <button> is invalid HTML. Tap-to-open lives
  // on the div; the toggle stops propagation so it doesn't also open the modal.
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      class="md:hidden w-full text-left px-3 py-2.5 border-b border-[color:var(--color-outline-variant)]/60 bg-[color:var(--color-surface-container-lowest)] active:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-inset transition-colors flex flex-col gap-1.5 cursor-pointer"
      style={{ minHeight: "56px" }}
    >
      <div class="flex items-center gap-2">
        {task.is_milestone ? (
          <Diamond
            size={14}
            class="text-[color:var(--color-prio-urgent)] shrink-0"
            fill="currentColor"
            aria-hidden="true"
          />
        ) : onToggleDone && !readOnly ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleDone(e);
            }}
            class="-ml-1 shrink-0 inline-flex items-center justify-center rounded-full text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            style={{ width: "32px", height: "32px" }}
            aria-label={isDone ? "Aufgabe wieder öffnen" : "Aufgabe als erledigt markieren"}
            aria-pressed={isDone}
          >
            {isDone ? (
              <span
                class="inline-flex items-center justify-center size-4 rounded-sm text-[color:var(--color-on-accent)]"
                style={{ background: TASK_STATUS_VAR.done }}
              >
                <Check size={14} strokeWidth={3} aria-hidden="true" />
              </span>
            ) : (
              <span
                class="inline-block size-3.5 rounded-sm border-2"
                style={{ borderColor: TASK_STATUS_VAR[task.status] }}
              />
            )}
          </button>
        ) : (
          <span
            class="inline-block size-2.5 rounded-sm border shrink-0"
            style={{
              borderColor: TASK_STATUS_VAR[task.status],
              background:
                task.status === "done" ? TASK_STATUS_VAR.done : "transparent",
            }}
            aria-hidden="true"
          />
        )}
        {isBlocked && (
          <AlertCircle
            size={14}
            class="text-[color:var(--color-status-blocked)] shrink-0"
            aria-label="Blockiert"
          />
        )}
        <span
          class={`flex-1 min-w-0 truncate text-sm text-[color:var(--color-on-surface)] ${
            task.status === "done" ? "line-through opacity-70" : ""
          }`}
        >
          {task.title}
        </span>
        {task.ticket && (
          <span
            class="font-mono text-label font-medium text-[color:var(--color-on-surface-variant)] shrink-0"
            title="Ticketnummer"
          >
            {task.ticket}
          </span>
        )}
        <AiOriginBadge origin={task.ai_origin} />
        {task.attachment_count > 0 && (
          <span
            class="shrink-0 inline-flex items-center gap-0.5 text-label text-[color:var(--color-on-surface-variant)]"
            aria-label={`${task.attachment_count} Anhänge`}
          >
            <Paperclip size={14} aria-hidden="true" />
            {task.attachment_count}
          </span>
        )}
      </div>
      <div class="flex flex-wrap items-center gap-1.5 pl-[18px]">
        <span
          class="inline-flex items-center rounded-md px-2 py-0.5 text-label font-semibold"
          style={softPillStyle(TASK_STATUS_VAR[task.status])}
        >
          {TASK_STATUS_LABEL[task.status]}
        </span>
        {task.priority && (
          <span
            class="inline-flex items-center rounded-md px-2 py-0.5 text-label font-semibold"
            style={softPillStyle(TASK_PRIORITY_VAR[task.priority])}
          >
            <Flag size={14} class="shrink-0" fill="currentColor" aria-hidden="true" />
            {TASK_PRIORITY_LABEL[task.priority]}
          </span>
        )}
        {due && (
          <span class="inline-flex items-center gap-1 text-label text-[color:var(--color-on-surface-variant)]">
            <Calendar size={14} aria-hidden="true" />
            {formatDateShort(due)}
          </span>
        )}
        {labels.slice(0, 2).map((l) => (
          <LabelChip key={l.id} name={l.name} color={l.color} compact />
        ))}
        {labels.length > 2 && (
          <span class="text-label text-[color:var(--color-on-surface-variant)]">
            +{labels.length - 2}
          </span>
        )}
        {assignees.length > 0 && (
          <span class="ml-auto inline-flex items-center gap-0.5">
            {assignees.map((u) => (
              <UserAvatar
                key={u.id}
                userId={u.id}
                name={u.name}
                avatarUrl={u.avatar_url}
                size={20}
              />
            ))}
            {extraAssignees > 0 && (
              <span class="inline-flex items-center justify-center size-5 rounded-full bg-[color:var(--color-surface-container)] text-label font-medium text-[color:var(--color-on-surface-variant)] border border-[color:var(--color-surface-container-lowest)]">
                +{extraAssignees}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Komfort-Status pill. When the project uses dynamic statuses (statuses prop
 * provided + task has statusInfo), renders the custom status name/color and
 * offers the project's status list in the dropdown (calling onChangeId).
 * Falls back to the legacy enum display and onChange when statusInfo is absent.
 */
function StatusPill(props: {
  value: TaskStatus;
  statusInfo?: StatusInfo | null;
  statuses?: Status[];
  onChange: (v: TaskStatus) => void | Promise<void>;
  onChangeId?: (id: number) => void | Promise<void>;
  readOnly?: boolean;
}): preact.JSX.Element {
  const { statusInfo, statuses, readOnly } = props;
  const useDynamic = statusInfo != null && statuses && statuses.length > 0;

  const displayName = useDynamic ? statusInfo.name : TASK_STATUS_LABEL[props.value];
  const displayColor = useDynamic ? statusInfo.color : TASK_STATUS_VAR[props.value];
  // The select's controlled value: id (string) for dynamic, enum for legacy.
  const selectValue = useDynamic ? String(statusInfo.id) : props.value;

  return (
    <div class="juuplan-status-pill group/pill relative inline-flex items-center justify-self-start">
      <span
        class="inline-flex items-center justify-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors"
        // softPillStyle statt roher Statusfarbe als Text: frei gewaehlte, helle
        // Statusfarben (Server-Standard #10B981) lagen sonst bei ~2:1.
        style={{ ...softPillStyle(displayColor), minHeight: "26px" }}
      >
        <span
          class="inline-block size-1.5 rounded-full shrink-0"
          style={{ background: displayColor }}
          aria-hidden="true"
        />
        {displayName}
      </span>
      {!readOnly && (
        <select
          value={selectValue}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            if (useDynamic && props.onChangeId) {
              void props.onChangeId(Number(v));
            } else {
              void props.onChange(v as TaskStatus);
            }
          }}
          class="peer absolute inset-0 w-full h-full opacity-0 cursor-pointer focus:outline-none"
          aria-label="Status"
        >
          {useDynamic
            ? (statuses ?? []).map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))
            : (["backlog", "in_progress", "review", "done", "blocked"] as TaskStatus[]).map((s) => (
                <option key={s} value={s}>
                  {TASK_STATUS_LABEL[s]}
                </option>
              ))}
        </select>
      )}
        {/* Bedienbarkeits-Hinweis: der Auswahl-Select liegt unsichtbar
            (`opacity-0`) über dem Pill — ohne Chevron sah die Fläche wie ein
            reines Anzeige-Etikett aus. Erscheint bei Hover und bei Fokus des
            Selects (`peer-focus`), damit auch Tastaturbedienung ihn sieht. */}
        {!readOnly && (
          <ChevronDown
            size={14}
            class="pointer-events-none shrink-0 -ml-0.5 opacity-0 transition-opacity group-hover/pill:opacity-60 peer-focus:opacity-100 text-[color:var(--color-on-surface-variant)]"
            aria-hidden="true"
          />
        )}
    </div>
  );
}

/**
 * PRIORITÄTSSYMBOL DER GANZEN APP: DIE FAHNE.
 *
 * Vorher trug die Priorität in `TaskRow`/`TaskRowKompakt`/`TaskCard`/
 * `RecordCard` gar kein Symbol (nur einen Text-Pill), in `RecordRow` und
 * `RecordStepList` dagegen eine Fahne — beim Umschalten Liste/Board auf
 * DERSELBEN Seite wechselte damit das Formvokabular.
 *
 * Gewählt ist die Fahne, nicht der Diamant: der Diamant ist in dieser App
 * bereits der MEILENSTEIN-Marker (`task.is_milestone`, dieselbe Zeile, wenige
 * Zentimeter weiter links — und es ist die Gantt-Konvention). Ein Diamant für
 * die Priorität würde direkt mit ihm kollidieren.
 *
 * Ungefüllt (`fill="none"`) heißt „keine Priorität gesetzt" — dasselbe
 * Ghost-Muster, das `RecordRow` schon benutzt.
 */
function PriorityPill(props: {
  value: TaskPriority | null;
  onChange: (v: TaskPriority | null) => void | Promise<void>;
  readOnly?: boolean;
}): preact.JSX.Element {
  const filled = props.value !== null;
  return (
    <div class="juuplan-priority-pill group/pill relative inline-flex items-center justify-self-start">
      <span
        class={`inline-flex items-center justify-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
          filled
            ? ""
            : "text-[color:var(--color-on-surface-variant)] border border-dashed border-[color:var(--color-outline-variant)]"
        }`}
        style={
          filled
            ? {
                background: `color-mix(in srgb, ${TASK_PRIORITY_VAR[props.value as TaskPriority]} 14%, transparent)`,
                color: TASK_PRIORITY_VAR[props.value as TaskPriority],
                minHeight: "26px",
              }
            : { minHeight: "26px" }
        }
      >
        {/* Eine Fahne für JEDE Prioritätsanzeige der App — siehe Kommentar
            über `PriorityPill`. Ungefüllt, wenn keine Priorität gesetzt ist:
            dasselbe Ghost-Muster wie in `RecordRow`. */}
        <Flag
          size={14}
          class="shrink-0"
          fill={filled ? "currentColor" : "none"}
          aria-hidden="true"
        />
        {filled ? TASK_PRIORITY_LABEL[props.value as TaskPriority] : "—"}
      </span>
      {!props.readOnly && (
        <select
          value={props.value ?? ""}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value as TaskPriority | "";
            void props.onChange(v === "" ? null : v);
          }}
          class="peer absolute inset-0 w-full h-full opacity-0 cursor-pointer focus:outline-none"
          aria-label="Priorität"
        >
          <option value="">—</option>
          {(["low", "medium", "high", "urgent"] as TaskPriority[]).map((p) => (
            <option key={p} value={p}>
              {TASK_PRIORITY_LABEL[p]}
            </option>
          ))}
        </select>
      )}
        {/* Bedienbarkeits-Hinweis: der Auswahl-Select liegt unsichtbar
            (`opacity-0`) über dem Pill — ohne Chevron sah die Fläche wie ein
            reines Anzeige-Etikett aus. Erscheint bei Hover und bei Fokus des
            Selects (`peer-focus`), damit auch Tastaturbedienung ihn sieht. */}
        {!props.readOnly && (
          <ChevronDown
            size={14}
            class="pointer-events-none shrink-0 -ml-0.5 opacity-0 transition-opacity group-hover/pill:opacity-60 peer-focus:opacity-100 text-[color:var(--color-on-surface-variant)]"
            aria-hidden="true"
          />
        )}
    </div>
  );
}

function LabelsCell(props: {
  labels: LabelItem[];
  onOpen: () => void;
}): preact.JSX.Element {
  const { labels, onOpen } = props;
  const visible = labels.slice(0, 2);
  const overflow = labels.length - visible.length;
  return (
    <button
      type="button"
      onClick={onOpen}
      class="w-full inline-flex items-center gap-1 px-1 -mx-1 rounded text-left hover:bg-[color:var(--color-surface-container)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors overflow-hidden min-h-[28px]"
      aria-label={labels.length === 0 ? "Labels — keine" : `Labels: ${labels.map((l) => l.name).join(", ")}`}
      title={labels.length === 0 ? "Labels (im Modal bearbeiten)" : labels.map((l) => l.name).join(", ")}
    >
      {labels.length === 0 ? (
        <span class="text-label text-[color:var(--color-on-surface-variant)]/60">—</span>
      ) : (
        <>
          {visible.map((l) => (
            <LabelChip key={l.id} name={l.name} color={l.color} compact />
          ))}
          {overflow > 0 && (
            <span class="shrink-0 inline-flex items-center rounded px-1 py-0 text-label font-medium leading-4 bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]">
              +{overflow}
            </span>
          )}
        </>
      )}
    </button>
  );
}
